import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Repeat, Plus, FileText, Ban, CalendarPlus, Copy } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { api, formatPrice, formatDate } from "@/lib/api";

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const SERVICE_TIER_OPTS = [
  ["student-onsite", "Student · On-Site"],
  ["student-athome", "Student · At Home"],
  ["student-virtual", "Student · Virtual"],
  ["adult-onsite", "Adult · On-Site"],
  ["adult-athome", "Adult · At Client Location"],
  ["adult-virtual", "Adult · Virtual"],
];

export default function RecurringPatternsPanel({ gcalConnected = false, onMaterialized } = {}) {
  const [creating, setCreating] = useState(false);
  const [duplicateSeed, setDuplicateSeed] = useState(null);   // pre-filled draft for NewPatternDialog
  const [endingFor, setEndingFor] = useState(null);           // pattern being ended (opens dialog)
  const [generatingFor, setGeneratingFor] = useState(null);
  const [materializingFor, setMaterializingFor] = useState(null);
  const [editingCurriculumFor, setEditingCurriculumFor] = useState(null);
  const { data: patterns = [], refetch } = useQuery({
    queryKey: ["adminRecurringPatterns"],
    queryFn: async () => (await api.get("/admin/recurring-patterns")).data,
  });

  const startDuplicate = (p) => {
    const today = new Date();
    const iso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    setDuplicateSeed({
      label: p.label ? `${p.label} (copy)` : "",
      parent_name: p.parent_name || "",
      parent_email: p.parent_email || "",
      parent_phone: p.parent_phone || "",
      parent_address: p.parent_address || "",
      students: (p.students && p.students.length ? p.students : [{ student_name: "", esa_contract_number: "" }])
        .map(s => ({ student_name: s.student_name || "", esa_contract_number: s.esa_contract_number || "" })),
      service_type: p.service_type || "student-onsite",
      duration_minutes: p.duration_minutes || 90,
      weekdays: (p.weekdays && p.weekdays.length ? p.weekdays : (p.weekday != null ? [p.weekday] : [1])),
      session_start_time: p.session_start_time || "09:00",
      rate_per_session: p.rate_per_session ?? 200,
      payment_method: p.payment_method || "classwallet",
      classwallet_fee_percent: p.classwallet_fee_percent ?? 2.04,
      start_date: iso,
      end_date: "",
      notes: p.notes || "",
      curriculum_box: p.curriculum_box || "",
      esa_alignment: p.esa_alignment || "",
    });
    setCreating(true);
  };

  const setAddressPrompt = async (p) => {
    const next = window.prompt(
      `Parent mailing address for ${p.label || p.parent_name}\n\nThis appears on the ClassWallet invoice Bill-To block. Already-generated invoices won't retroactively update.`,
      p.parent_address || ""
    );
    if (next === null) return;
    try {
      await api.patch(`/admin/recurring-patterns/${p.id}`, { parent_address: next.trim() });
      toast.success("Address saved");
      refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save address"); }
  };

  const setSessionTimePrompt = async (p) => {
    const next = window.prompt(
      `Session start time (24-hour HH:MM, Arizona local) for ${p.label || p.parent_name}\n\nExample: 14:00 for 2:00 PM. This appears on every invoice line item. Already-generated invoices won't retroactively update.`,
      p.session_start_time || "09:00"
    );
    if (next === null) return;
    const val = next.trim();
    if (!/^\d{1,2}:\d{2}$/.test(val)) {
      toast.error("Use HH:MM 24-hour format (e.g. 14:00 for 2 PM)");
      return;
    }
    try {
      await api.patch(`/admin/recurring-patterns/${p.id}`, { session_start_time: val });
      toast.success("Session time saved");
      refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save session time"); }
  };

  return (
    <details className="rounded-2xl border border-gg-border bg-gg-surface p-4" data-testid="recurring-patterns-panel" open>
      <summary className="cursor-pointer flex items-center justify-between">
        <span className="font-heading text-lg text-[var(--gg-text)] inline-flex items-center gap-2">
          <Repeat size={16} className="text-gg-green" /> Recurring students
          <span className="text-xs text-gg-muted font-sans">({patterns.length})</span>
        </span>
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); setCreating(true); }}
          className="btn-pill btn-primary text-xs"
          data-testid="recurring-new-btn"
        >
          <Plus size={12} /> New pattern
        </button>
      </summary>
      <div className="mt-3">
        {patterns.length === 0 ? (
          <p className="text-sm text-gg-muted italic">
            No recurring students yet. Create a pattern for families who meet on a weekly schedule (e.g. Roy &amp; Ian every Tuesday).
          </p>
        ) : (
          <div className="space-y-2">
            {patterns.map((p) => (
              <div key={p.id} className="rounded-lg border border-gg-border bg-gg-bg p-3 flex items-start justify-between gap-3 flex-wrap" data-testid={`recurring-row-${p.id}`}>
                <div className="text-sm flex-1 min-w-[240px]">
                  <div className="font-semibold">{p.label || `${p.parent_name} — ${WEEKDAYS[p.weekday]}s`}</div>
                  <div className="text-xs text-gg-muted mt-0.5">
                    {p.students.map(s => s.student_name).join(" & ")} · {(p.weekdays && p.weekdays.length ? p.weekdays : [p.weekday]).map(w => WEEKDAYS[w] + "s").join(" & ")} ·
                    {p.duration_minutes}min ·
                    {p.session_start_time ? (
                      <button type="button" onClick={() => setSessionTimePrompt(p)} className="hover:text-gg-green underline underline-offset-2" data-testid={`recurring-time-${p.id}`}>
                        {" "}{p.session_start_time}
                      </button>
                    ) : (
                      <button type="button" onClick={() => setSessionTimePrompt(p)} className="text-[#B24C4C] hover:underline" data-testid={`recurring-set-time-${p.id}`}>
                        {" "}set time
                      </button>
                    )}
                    {" "}· {formatPrice(p.rate_per_session)}/session/student
                    {p.classwallet_fee_percent > 0 && p.payment_method === "classwallet" && ` · +${p.classwallet_fee_percent}% CW fee`}
                  </div>
                  <div className="text-[11px] text-gg-muted mt-0.5">
                    Started {formatDate(p.start_date)}
                    {p.end_date ? ` · ends ${formatDate(p.end_date)}` : " · open-ended"}
                    {" · "}<span className={p.status === "active" ? "text-gg-green" : "text-[#9b6a26]"}>{p.status}</span>
                  </div>
                  {p.status === "ended" && p.end_reason ? (
                    <div className="text-[11px] text-[#9b6a26] mt-0.5" data-testid={`recurring-end-reason-${p.id}`}>
                      Reason: {p.end_reason}
                    </div>
                  ) : null}
                  {!p.parent_address ? (
                    <button
                      type="button"
                      onClick={() => setAddressPrompt(p)}
                      className="text-[11px] text-[#B24C4C] mt-0.5 hover:underline text-left"
                      data-testid={`recurring-no-address-${p.id}`}
                    >
                      ⚠ No mailing address on file — click to add (invoices need this)
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setAddressPrompt(p)}
                      className="text-[11px] text-gg-muted mt-0.5 hover:text-gg-green text-left"
                      data-testid={`recurring-address-${p.id}`}
                    >
                      📮 {p.parent_address}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setEditingCurriculumFor(p)}
                    className={`text-[11px] mt-0.5 hover:underline text-left block ${(p.curriculum_box || "").trim() ? "text-gg-muted hover:text-gg-green" : "text-[#B24C4C]"}`}
                    data-testid={`recurring-curriculum-${p.id}`}
                  >
                    {(p.curriculum_box || "").trim()
                      ? `📚 Curriculum: ${(p.curriculum_box || "").slice(0, 60)}${(p.curriculum_box || "").length > 60 ? "…" : ""}`
                      : "⚠ No curriculum on file — click to add (shows in invoice CURRICULUM box)"}
                  </button>
                </div>
                <div className="flex flex-col gap-1.5 items-end">
                  <button
                    type="button"
                    onClick={() => setMaterializingFor(p)}
                    className="btn-pill btn-outline text-xs"
                    data-testid={`recurring-materialize-${p.id}`}
                  >
                    <CalendarPlus size={12} /> Add sessions to Bookings
                  </button>
                  <button
                    type="button"
                    onClick={() => setGeneratingFor(p)}
                    className="btn-pill btn-outline text-xs"
                    data-testid={`recurring-generate-${p.id}`}
                  >
                    <FileText size={12} /> Generate month invoice
                  </button>
                  <button
                    type="button"
                    onClick={() => startDuplicate(p)}
                    className="btn-pill btn-outline text-xs"
                    data-testid={`recurring-duplicate-${p.id}`}
                    title="Copy all details into a new editable draft"
                  >
                    <Copy size={12} /> Duplicate
                  </button>
                  {p.status === "active" && (
                    <button
                      type="button"
                      onClick={() => setEndingFor(p)}
                      className="text-[11px] text-gg-muted hover:text-[#B24C4C] inline-flex items-center gap-1"
                      data-testid={`recurring-end-${p.id}`}
                    >
                      <Ban size={10} /> End pattern
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {creating && (
        <NewPatternDialog
          seed={duplicateSeed}
          onClose={() => { setCreating(false); setDuplicateSeed(null); }}
          onSaved={() => { setCreating(false); setDuplicateSeed(null); refetch(); }}
        />
      )}
      {endingFor && (
        <EndPatternDialog
          pattern={endingFor}
          onClose={() => setEndingFor(null)}
          onSaved={() => { setEndingFor(null); refetch(); }}
        />
      )}
      {generatingFor && (
        <GenerateInvoiceDialog
          pattern={generatingFor}
          onClose={() => setGeneratingFor(null)}
        />
      )}
      {materializingFor && (
        <MaterializeSessionsDialog
          pattern={materializingFor}
          gcalConnected={gcalConnected}
          onClose={() => setMaterializingFor(null)}
          onDone={() => { setMaterializingFor(null); onMaterialized && onMaterialized(); }}
        />
      )}
      {editingCurriculumFor && (
        <EditCurriculumDialog
          pattern={editingCurriculumFor}
          onClose={() => setEditingCurriculumFor(null)}
          onSaved={() => { setEditingCurriculumFor(null); refetch(); }}
        />
      )}
    </details>
  );
}

function NewPatternDialog({ seed, onClose, onSaved }) {
  const defaults = {
    label: "",
    parent_name: "", parent_email: "", parent_phone: "", parent_address: "",
    students: [{ student_name: "", esa_contract_number: "" }],
    service_type: "student-onsite",
    duration_minutes: 90,
    weekdays: [1],   // list of ints; single-day patterns pass [1]
    session_start_time: "09:00",
    rate_per_session: 200,
    payment_method: "classwallet",
    classwallet_fee_percent: 2.04,
    start_date: "",
    end_date: "",
    notes: "",
    curriculum_box: "",
    esa_alignment: "",
  };
  const [form, setForm] = useState(seed ? { ...defaults, ...seed } : defaults);
  const [busy, setBusy] = useState(false);
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setS = (i, k, v) => setForm((f) => ({
    ...f, students: f.students.map((s, idx) => idx === i ? { ...s, [k]: v } : s),
  }));
  const addStudent = () => form.students.length < 4 && setF("students", [...form.students, { student_name: "", esa_contract_number: "" }]);
  const rmStudent = (i) => form.students.length > 1 && setF("students", form.students.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!form.parent_email || !form.parent_name || !form.start_date) {
      toast.error("Parent, email, and start date are required"); return;
    }
    const students = form.students.filter(s => s.student_name.trim());
    if (students.length === 0) { toast.error("At least one student required"); return; }
    setBusy(true);
    try {
      await api.post("/admin/recurring-patterns", {
        ...form,
        students,
        end_date: form.end_date || null,
        rate_per_session: Number(form.rate_per_session),
        classwallet_fee_percent: Number(form.classwallet_fee_percent),
      });
      toast.success("Pattern created");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-gg-surface max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">{seed ? "Duplicate recurring pattern" : "New recurring pattern"}</DialogTitle>
          <DialogDescription>{seed ? "All details copied from the source pattern — edit any field before saving. Choose a new start date." : "Set up a weekly schedule for a family. You'll generate a monthly invoice on-demand."}</DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <Label>Label (optional)</Label>
            <Input value={form.label} onChange={(e) => setF("label", e.target.value)} placeholder="e.g. Roy & Ian — Weekly Tuesdays" className="mt-1.5" data-testid="pat-label" />
          </div>
          <div><Label>Parent name *</Label><Input value={form.parent_name} onChange={(e) => setF("parent_name", e.target.value)} className="mt-1.5" data-testid="pat-parent-name" /></div>
          <div><Label>Parent email *</Label><Input type="email" value={form.parent_email} onChange={(e) => setF("parent_email", e.target.value)} className="mt-1.5" data-testid="pat-parent-email" /></div>
          <div className="sm:col-span-2"><Label>Parent phone</Label><Input value={form.parent_phone} onChange={(e) => setF("parent_phone", e.target.value)} className="mt-1.5" /></div>
          <div className="sm:col-span-2">
            <Label>Parent mailing address <span className="text-gg-muted font-normal">(shown on the ClassWallet invoice)</span></Label>
            <Input value={form.parent_address} onChange={(e) => setF("parent_address", e.target.value)} placeholder="123 Main St, Chino Valley, AZ 86323" className="mt-1.5" data-testid="pat-parent-address" />
          </div>
          <div className="sm:col-span-2 border-t border-gg-border pt-3">
            <div className="flex items-center justify-between mb-2">
              <Label>Students (1–4, split evenly)</Label>
              <button type="button" onClick={addStudent} disabled={form.students.length >= 4} className="btn-pill btn-outline text-[11px] disabled:opacity-40" data-testid="pat-add-student"><Plus size={11}/> Add</button>
            </div>
            {form.students.map((s, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 mb-2">
                <Input placeholder={`Student ${i + 1} name`} value={s.student_name} onChange={(e) => setS(i, "student_name", e.target.value)} data-testid={`pat-student-name-${i}`} />
                <Input placeholder="ESA #" value={s.esa_contract_number} onChange={(e) => setS(i, "esa_contract_number", e.target.value)} data-testid={`pat-student-esa-${i}`} />
                <button type="button" onClick={() => rmStudent(i)} disabled={form.students.length === 1} className="text-[#B24C4C] disabled:opacity-30 px-2">×</button>
              </div>
            ))}
          </div>
          <div>
            <Label>Service tier</Label>
            <select value={form.service_type} onChange={(e) => setF("service_type", e.target.value)} className="mt-1.5 w-full h-10 rounded-md border border-gg-border bg-gg-surface px-3 text-sm">
              {SERVICE_TIER_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <Label>Duration (min)</Label>
            <select value={form.duration_minutes} onChange={(e) => setF("duration_minutes", Number(e.target.value))} className="mt-1.5 w-full h-10 rounded-md border border-gg-border bg-gg-surface px-3 text-sm">
              <option value={60}>60</option>
              <option value={90}>90</option>
            </select>
          </div>
          <div>
            <Label>Meets every</Label>
            <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="pat-weekdays">
              {WEEKDAYS.map((w, i) => {
                const on = (form.weekdays || []).includes(i);
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => {
                      const set = new Set(form.weekdays || []);
                      if (set.has(i)) set.delete(i); else set.add(i);
                      const next = Array.from(set).sort((a, b) => a - b);
                      setF("weekdays", next.length ? next : [i]);   // never empty
                    }}
                    className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${on ? "bg-gg-green text-white border-gg-green" : "bg-gg-surface border-gg-border text-gg-muted hover:border-gg-green"}`}
                    data-testid={`pat-weekday-${i}`}
                  >
                    {w.slice(0, 3)}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-gg-muted mt-1">Pick one or more — sessions materialize on each selected day.</p>
          </div>
          <div>
            <Label>Session start time (Arizona local)</Label>
            <Input type="time" value={form.session_start_time} onChange={(e) => setF("session_start_time", e.target.value)} className="mt-1.5" data-testid="pat-session-time" />
            <p className="text-[11px] text-gg-muted mt-1">Shown on every invoice line (e.g. 2:00pm).</p>
          </div>
          <div>
            <Label>Rate per session ($) — charged to each student individually</Label>
            <Input type="number" step="0.01" min="0.01" value={form.rate_per_session} onChange={(e) => setF("rate_per_session", e.target.value)} className="mt-1.5" data-testid="pat-rate" />
          </div>
          <div>
            <Label>Payment method</Label>
            <select value={form.payment_method} onChange={(e) => setF("payment_method", e.target.value)} className="mt-1.5 w-full h-10 rounded-md border border-gg-border bg-gg-surface px-3 text-sm">
              <option value="classwallet">ClassWallet</option>
              <option value="direct">Direct pay (Stripe)</option>
            </select>
          </div>
          <div>
            <Label>ClassWallet fee % (only if CW)</Label>
            <Input type="number" step="0.01" min="0" value={form.classwallet_fee_percent} onChange={(e) => setF("classwallet_fee_percent", e.target.value)} className="mt-1.5" data-testid="pat-cw-fee" />
          </div>
          <div>
            <Label>Start date *</Label>
            <Input type="date" value={form.start_date} onChange={(e) => setF("start_date", e.target.value)} className="mt-1.5" data-testid="pat-start-date" />
          </div>
          <div>
            <Label>End date (leave blank if open-ended)</Label>
            <Input type="date" value={form.end_date} onChange={(e) => setF("end_date", e.target.value)} className="mt-1.5" />
          </div>
          <div className="sm:col-span-2">
            <Label>Curriculum <span className="text-gg-muted font-normal">(shown on every invoice CURRICULUM box)</span></Label>
            <Textarea rows={3} value={form.curriculum_box} onChange={(e) => setF("curriculum_box", e.target.value)} className="mt-1.5" placeholder="e.g. Weekly homestead skills — livestock care, garden planning, from-scratch cooking, math + science integrated…" data-testid="pat-curriculum" />
          </div>
          <div className="sm:col-span-2">
            <Label>Arizona academic-standards alignment <span className="text-gg-muted font-normal">(optional — appears in the ARIZONA ACADEMIC STANDARDS ALIGNMENT block)</span></Label>
            <Textarea rows={3} value={form.esa_alignment} onChange={(e) => setF("esa_alignment", e.target.value)} className="mt-1.5" placeholder="e.g. Aligns with AZ Science 5.L1U1.6, Practical Life Skills (Family & Consumer Sciences), Hands-On Project-Based Learning…" data-testid="pat-alignment" />
          </div>
          <div className="sm:col-span-2">
            <Label>Notes (private)</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setF("notes", e.target.value)} className="mt-1.5" />
          </div>
        </div>
        <DialogFooter>
          <button type="button" onClick={onClose} className="btn-pill btn-outline">Cancel</button>
          <button type="button" onClick={save} disabled={busy} className="btn-pill btn-primary disabled:opacity-60" data-testid="pat-save">
            {busy ? "Saving…" : "Create pattern"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GenerateInvoiceDialog({ pattern, onClose }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [studentIndex, setStudentIndex] = useState(0);
  const [busy, setBusy] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [result, setResult] = useState(null);

  const { data: preview } = useQuery({
    queryKey: ["patternPreview", pattern.id, year, month],
    queryFn: async () => (await api.get(`/admin/recurring-patterns/${pattern.id}/preview?year=${year}&month=${month}`)).data,
  });

  const openPreviewPdf = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.get(`/admin/recurring-patterns/${pattern.id}/preview-invoice-url?year=${year}&month=${month}&student_index=${studentIndex}`);
      // Open in a new tab; browser will render the inline PDF response.
      window.open(data.url, "_blank", "noopener");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally {
      setPreviewing(false);
    }
  };

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/recurring-patterns/${pattern.id}/generate-invoice?year=${year}&month=${month}`);
      setResult(data);
      toast.success(`Generated ${data.created.length} invoice(s), skipped ${data.skipped.length}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Generate failed"); }
    finally { setBusy(false); }
  };

  const students = pattern.students || [];
  const multiStudent = students.length > 1;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-gg-surface max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Generate monthly invoice</DialogTitle>
          <DialogDescription>{pattern.label || `${pattern.parent_name} — ${(pattern.weekdays&&pattern.weekdays.length?pattern.weekdays:[pattern.weekday]).map(w=>WEEKDAYS[w]).join(" & ")}s`}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Year</Label>
            <Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} className="mt-1.5" data-testid="gen-year" />
          </div>
          <div>
            <Label>Month</Label>
            <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className="mt-1.5 w-full h-10 rounded-md border border-gg-border bg-gg-surface px-3 text-sm" data-testid="gen-month">
              {["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
            </select>
          </div>
          {multiStudent && (
            <div className="col-span-2">
              <Label>Preview PDF for student</Label>
              <select
                value={studentIndex}
                onChange={(e) => setStudentIndex(Number(e.target.value))}
                className="mt-1.5 w-full h-10 rounded-md border border-gg-border bg-gg-surface px-3 text-sm"
                data-testid="gen-preview-student"
              >
                {students.map((s, i) => (
                  <option key={i} value={i}>{s.student_name || `Student ${i + 1}`}</option>
                ))}
              </select>
              <p className="text-[11px] text-gg-muted mt-1">Preview shows one student at a time. Generating still creates one invoice per student.</p>
            </div>
          )}
        </div>
        {preview && (
          <div className="mt-4 rounded-lg border border-gg-border bg-gg-bg p-3 text-sm space-y-1" data-testid="gen-preview">
            <div className="font-semibold">Preview — {preview.session_count} session{preview.session_count !== 1 ? "s" : ""} in this month</div>
            {preview.sessions.map((s) => (
              <div key={s} className="text-xs text-gg-muted">· {new Date(s + "T12:00:00").toLocaleDateString("en-US", {weekday:"short", month:"short", day:"numeric"})}</div>
            ))}
            <div className="border-t border-gg-border pt-2 mt-2">
              <div className="flex justify-between text-xs"><span>Subtotal per student ({preview.session_count} × {formatPrice(preview.rate_per_session_per_student)}):</span><span>{formatPrice(preview.subtotal_per_student)}</span></div>
              {preview.classwallet_fee_per_student > 0 && (
                <div className="flex justify-between text-xs"><span>ClassWallet fee per student:</span><span>{formatPrice(preview.classwallet_fee_per_student)}</span></div>
              )}
              <div className="flex justify-between font-semibold"><span>Total per student:</span><span>{formatPrice(preview.total_per_student)}</span></div>
              <div className="flex justify-between text-xs text-gg-muted"><span>Grand total ({preview.students.length} students):</span><span>{formatPrice(preview.grand_total)}</span></div>
            </div>
            <button
              type="button"
              onClick={openPreviewPdf}
              disabled={previewing || !preview.session_count}
              className="btn-pill btn-outline text-xs mt-2 disabled:opacity-60"
              data-testid="gen-preview-pdf"
              title="Open the exact PDF layout in a new tab — nothing is saved"
            >
              <FileText size={12} /> {previewing ? "Opening…" : "Preview PDF"}
            </button>
          </div>
        )}
        {result && (
          <div className="mt-4 rounded-lg border border-gg-green/30 bg-gg-green/5 p-3 text-sm" data-testid="gen-result">
            <div className="font-semibold text-gg-green mb-1">✓ Created {result.created.length} invoice(s)</div>
            {result.created.map(r => (
              <div key={r.invoice_number} className="text-xs">{r.student}: invoice #{r.invoice_number} — {formatPrice(r.total)}</div>
            ))}
            {result.skipped.length > 0 && (
              <div className="text-xs text-[#9b6a26] mt-2">Skipped {result.skipped.length} (already generated for this month)</div>
            )}
            <p className="text-xs text-gg-muted mt-2 italic">Draft invoices created — find them on the Registrations tab and click &quot;View Invoice&quot; to print PDF for ClassWallet submission.</p>
          </div>
        )}
        <DialogFooter>
          <button type="button" onClick={onClose} className="btn-pill btn-outline">Close</button>
          {!result && (
            <button type="button" onClick={generate} disabled={busy || !preview?.session_count} className="btn-pill btn-primary disabled:opacity-60" data-testid="gen-confirm">
              {busy ? "Generating…" : "Generate invoices"}
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function MaterializeSessionsDialog({ pattern, gcalConnected, onClose, onDone }) {
  const today = new Date();
  const twelveWeeksOut = new Date(today.getTime() + 12 * 7 * 86400000);
  const toIso = (d) => {
    // local YYYY-MM-DD (avoids UTC off-by-one for late-evening Arizona)
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
  };
  const [fromDate, setFromDate] = useState(toIso(today));
  const [toDate, setToDate] = useState(toIso(twelveWeeksOut));
  const [startTime, setStartTime] = useState("09:00");
  const [createEvents, setCreateEvents] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/recurring-patterns/${pattern.id}/materialize-sessions`, {
        from_date: fromDate,
        to_date: toDate,
        start_time: startTime,
        create_calendar_events: createEvents,
      });
      setResult(data);
      const c = data.created.length;
      const s = data.skipped.length;
      if (c > 0) toast.success(`Added ${c} session${c !== 1 ? "s" : ""} to Bookings${data.calendar_synced ? " + Calendar" : ""}${s ? ` · ${s} already existed` : ""}`);
      else toast(s ? `${s} session${s !== 1 ? "s" : ""} already existed — nothing new to add` : "No sessions matched");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Materialize failed");
    } finally {
      setBusy(false);
    }
  };

  const closeAndRefresh = () => { onDone && onDone(); };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-gg-surface max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Add sessions to Bookings</DialogTitle>
          <DialogDescription>
            Auto-creates one confirmed booking row per matching weekday ({(pattern.weekdays && pattern.weekdays.length ? pattern.weekdays : [pattern.weekday]).map(w => WEEKDAYS[w]).join(" & ")}) in the date range.
            Each row shows on the Bookings tab and can optionally sync to Google Calendar.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>From</Label>
            <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="mt-1.5" data-testid="mat-from-date" />
          </div>
          <div>
            <Label>To</Label>
            <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="mt-1.5" data-testid="mat-to-date" />
          </div>
          <div className="col-span-2">
            <Label>Session start time (Arizona local)</Label>
            <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="mt-1.5" data-testid="mat-start-time" />
            <p className="text-xs text-gg-muted mt-1">{pattern.duration_minutes}-minute session duration (from pattern)</p>
          </div>
          <label className="col-span-2 flex items-start gap-2 text-sm mt-1">
            <input
              type="checkbox"
              checked={createEvents}
              onChange={(e) => setCreateEvents(e.target.checked)}
              disabled={!gcalConnected}
              className="mt-1"
              data-testid="mat-gcal-checkbox"
            />
            <span>
              <strong className="block text-[var(--gg-text)]">Create Google Calendar events</strong>
              <span className="text-gg-muted text-xs">
                {gcalConnected
                  ? "Each session appears on your calendar with an invite to the parent."
                  : "Connect Google Calendar first (banner at top of the admin page) to enable this."}
              </span>
            </span>
          </label>
        </div>
        {result && (
          <div className="mt-4 rounded-lg border border-gg-green/30 bg-gg-green/5 p-3 text-sm" data-testid="mat-result">
            <div className="font-semibold text-gg-green mb-1">✓ Added {result.created.length} booking{result.created.length !== 1 ? "s" : ""}</div>
            {result.created.slice(0, 8).map((c) => (
              <div key={c.booking_id} className="text-xs">
                · {new Date(c.date + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
                {c.calendar_event ? " · calendar ✓" : ""}
              </div>
            ))}
            {result.created.length > 8 && <div className="text-xs text-gg-muted">…and {result.created.length - 8} more</div>}
            {result.skipped.length > 0 && (
              <div className="text-xs text-[#9b6a26] mt-2">
                Skipped {result.skipped.length} (already exist for those dates)
              </div>
            )}
            {result.calendar_errors && result.calendar_errors.length > 0 && (
              <div className="text-xs text-[#B24C4C] mt-2">
                Calendar sync errors: {result.calendar_errors.slice(0, 3).join(" · ")}
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <button type="button" onClick={result ? closeAndRefresh : onClose} className="btn-pill btn-outline">
            {result ? "Done" : "Cancel"}
          </button>
          {!result && (
            <button type="button" onClick={submit} disabled={busy} className="btn-pill btn-primary disabled:opacity-60" data-testid="mat-confirm">
              {busy ? "Adding…" : "Add to Bookings"}
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditCurriculumDialog({ pattern, onClose, onSaved }) {
  const [curriculum, setCurriculum] = useState(pattern.curriculum_box || "");
  const [alignment, setAlignment] = useState(pattern.esa_alignment || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/recurring-patterns/${pattern.id}`, {
        curriculum_box: curriculum.trim(),
        esa_alignment: alignment.trim(),
      });
      toast.success("Curriculum saved — next invoice will use it");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-gg-surface max-h-[90vh] overflow-y-auto" data-testid="edit-curriculum-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Curriculum & academic alignment</DialogTitle>
          <DialogDescription>
            {pattern.label || `${pattern.parent_name} — ${(pattern.weekdays&&pattern.weekdays.length?pattern.weekdays:[pattern.weekday]).map(w=>WEEKDAYS[w]).join(" & ")}s`}. These fields
            populate the CURRICULUM box and ARIZONA ACADEMIC STANDARDS ALIGNMENT block on every future
            invoice generated from this pattern. Already-generated invoices won&apos;t retroactively update.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Curriculum</Label>
            <Textarea
              rows={5}
              value={curriculum}
              onChange={(e) => setCurriculum(e.target.value)}
              placeholder="e.g. Weekly homestead skills — livestock care, garden planning, from-scratch cooking, math + science integrated hands-on…"
              className="mt-1.5"
              data-testid="edit-curriculum-textarea"
            />
          </div>
          <div>
            <Label>Arizona academic-standards alignment</Label>
            <Textarea
              rows={5}
              value={alignment}
              onChange={(e) => setAlignment(e.target.value)}
              placeholder="e.g. Aligns with AZ Science 5.L1U1.6, Practical Life Skills (Family & Consumer Sciences), Hands-On Project-Based Learning…"
              className="mt-1.5"
              data-testid="edit-alignment-textarea"
            />
          </div>
        </div>
        <DialogFooter>
          <button type="button" onClick={onClose} className="btn-pill btn-outline">Cancel</button>
          <button type="button" onClick={save} disabled={busy} className="btn-pill btn-primary disabled:opacity-60" data-testid="edit-curriculum-save-btn">
            {busy ? "Saving…" : "Save"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function EndPatternDialog({ pattern, onClose, onSaved }) {
  const today = new Date();
  const iso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  const [endDate, setEndDate] = useState(pattern.end_date || iso);
  const [reason, setReason] = useState(pattern.end_reason || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!endDate) { toast.error("Pick an end date"); return; }
    setBusy(true);
    try {
      await api.patch(`/admin/recurring-patterns/${pattern.id}`, {
        status: "ended",
        end_date: endDate,
        end_reason: reason.trim(),
      });
      toast.success("Pattern ended");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not end pattern");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-gg-surface" data-testid="end-pattern-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">End pattern</DialogTitle>
          <DialogDescription>
            {pattern.label || pattern.parent_name} — no new sessions will be materialized past the end date.
            Existing invoices and bookings are unaffected.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>End date</Label>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="mt-1.5" data-testid="end-pattern-date" />
          </div>
          <div>
            <Label>Reason (optional, private)</Label>
            <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Family moved, switching to group classes, taking a break for summer…" className="mt-1.5" data-testid="end-pattern-reason" />
          </div>
        </div>
        <DialogFooter>
          <button type="button" onClick={onClose} className="btn-pill btn-outline">Cancel</button>
          <button type="button" onClick={save} disabled={busy} className="btn-pill btn-primary disabled:opacity-60" data-testid="end-pattern-confirm">
            {busy ? "Ending…" : "End pattern"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
